import { useMemo } from 'react';

import { settlementTypesClient } from 'app/api/apiClient/settlement/settlementTypesClient';
import { Bill } from 'app/api/apiClient/settlement/types';
import { queries } from 'app/constants/queries';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';

import { useHasBillPermissions } from './bills';

type ChargeCodeName =
  | 'additional_stops'
  | 'afterhour'
  | 'detention'
  | 'fuel_surcharge'
  | 'layover'
  | 'liftgate'
  | 'linehaul'
  | 'lumper_fees'
  | 'oversized'
  | 'reclassification'
  | 'redelivery'
  | 'reweigh';
type ImageCodeName =
  | 'bill_of_lading'
  | 'combined'
  | 'in_out_ticket'
  | 'invoice'
  | 'lumper_receipt'
  | 'other'
  | 'pod'
  | 'rate_confirmation'
  | 'scale_ticket';

export const useGetChargeTypesQuery = () => {
  const hasBillPermissions = useHasBillPermissions();

  return useQueryEnhanced(
    [queries.billsChargeTypes],
    settlementTypesClient.getChargeTypes,
    {
      enabled: hasBillPermissions,
    },
  );
};

export const useGetImageTypesQuery = () => {
  const hasBillPermissions = useHasBillPermissions();

  return useQueryEnhanced(
    [queries.billsImageTypes],
    settlementTypesClient.getImageTypes,
    {
      enabled: hasBillPermissions,
    },
  );
};

export const useGetChargeType = (codeName: ChargeCodeName) => {
  const { data: chargeTypesData } = useGetChargeTypesQuery();

  const chargeTypes = useMemo(() => chargeTypesData?.chargeTypes ?? [],
    [chargeTypesData]);

  return useMemo(() => chargeTypes.find(chargeType => chargeType.codeName === codeName),
    [chargeTypes, codeName]);
};

export const useGetChargeTypes = () => {
  const { data } = useGetChargeTypesQuery();

  return useMemo(() => {
    data?.chargeTypes.sort((a, b) => a.id > b.id ? 1 : -1);

    return data?.chargeTypes ?? [];
  },
  [data]);
};

export const useIsCombined = (imageTypeId: number) => {
  const { data: imageTypesData } = useGetImageTypesQuery();

  const imageTypes = useMemo(() => imageTypesData?.imageTypes ?? [],
    [imageTypesData]);

  return useMemo(() => imageTypeId === imageTypes.find(imageType => imageType.codeName === 'combined')?.id,
    [imageTypeId, imageTypes]);
};

export const useGetImageType = (codeName: ImageCodeName) => {
  const { data: imageTypesData } = useGetImageTypesQuery();

  const imageTypes = useMemo(() => imageTypesData?.imageTypes ?? [],
    [imageTypesData]);

  return useMemo(() => imageTypes.find(imageType => imageType.codeName === codeName),
    [imageTypes, codeName]);
};

export const useGetImageTypes = () => {
  const { data } = useGetImageTypesQuery();

  return useMemo(() => data?.imageTypes ?? [],
    [data]);
};

export const useGetChargeTypeFromBill = (bill: Bill | undefined, codeName: ChargeCodeName) => {
  const chargeType = useGetChargeType(codeName);

  return bill?.charges.find(charge => charge.chargeTypeId === chargeType?.id);
};

export const useGetChargeTypeFromId = (chargeTypeId: number) => {
  const chargeTypes = useGetChargeTypes();

  return useMemo(() => chargeTypes.find(({ id }) => id === chargeTypeId),
    [chargeTypeId, chargeTypes]);
};
